import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, Server, Globe, FileWarning, MailCheck } from "lucide-react";

/**
 * Politique de confidentialité — Mon Cabinet Bien-être
 * ------------------------------------------------------------------
 * Document factuel. Les points marqués "À VÉRIFIER" attendent une
 * confirmation officielle du support Emergent (hébergeur technique).
 * Rédaction prudente : aucune promesse absolue tant que le cadre
 * contractuel (DPA, transferts internationaux) n'est pas régularisé.
 */
export default function Privacy() {
  return (
    <div className="min-h-screen py-12 px-4" style={{ backgroundColor: "var(--surface)" }} data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-soft hover:text-sage mb-6" data-testid="privacy-back">
          <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
        </Link>

        <div className="card-soft p-8 sm:p-10 space-y-8">
          <header>
            <h1 className="font-display text-3xl sm:text-4xl mb-2">Politique de confidentialité & information RGPD</h1>
            <p className="text-sm text-muted-soft">Mon Cabinet Bien-être — Capsule Patrimoine</p>
            <p className="text-xs text-muted-soft mt-1">Dernière mise à jour : Février 2026</p>
          </header>

          {/* 1. Rôles */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3 flex items-center gap-2">
              <MailCheck className="w-5 h-5" strokeWidth={1.6} /> 1. Rôles au sens du RGPD
            </h2>
            <ul className="text-sm leading-relaxed space-y-2 list-disc pl-5">
              <li>
                <b>Responsable de traitement</b> : Capsule Patrimoine (Valérie Girin) — pour les données
                d'inscription, de facturation et de communication commerciale.
              </li>
              <li>
                <b>Responsable de traitement</b> : chaque thérapeute utilisateur — pour les données saisies
                dans son propre espace concernant ses clients (fiches, agenda, notes de séance, factures).
              </li>
              <li>
                <b>Emergent</b> intervient comme <b>fournisseur technique et hébergeur</b> de l'application
                (infrastructure cloud : Kubernetes, base MongoDB, sauvegardes, logs).
                La qualification contractuelle de son rôle au regard du RGPD ainsi que la formalisation
                du DPA restent à régulariser (voir section 8).
              </li>
            </ul>
          </section>

          {/* 2. Nature des données */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3">2. Nature des données traitées</h2>
            <p className="text-sm leading-relaxed mb-3">
              Mon Cabinet Bien-être est conçu pour traiter des <b>données administratives</b> et des
              <b> informations générales nécessaires au suivi de l'accompagnement</b>. L'application
              <b> n'est pas destinée au stockage de données de santé</b>. L'utilisateur est expressément
              invité à ne renseigner <b>aucune donnée médicale ou donnée de santé sensible</b> dans les
              champs libres.
            </p>
            <p className="text-sm leading-relaxed mb-3">Types de données collectées :</p>
            <ul className="text-sm leading-relaxed space-y-1.5 list-disc pl-5">
              <li><b>Compte thérapeute</b> : email, nom, profession, SIRET, adresse professionnelle, téléphone, IBAN</li>
              <li><b>Fiches clients</b> : nom, coordonnées, adresse, motif d'accompagnement, objectifs, notes générales</li>
              <li><b>Agenda</b> : rendez-vous, créneaux, statut de paiement</li>
              <li><b>Facturation</b> : factures émises, dépenses professionnelles</li>
              <li><b>Notes de séance</b> : observations d'accompagnement, exercices proposés, conseils, points de suivi</li>
              <li><b>Paiement</b> : Stripe (aucune donnée bancaire stockée sur notre infrastructure)</li>
            </ul>
          </section>

          {/* 3. Données de santé — Point critique */}
          <section className="rounded-xl border-2 p-5" style={{ background: "#FFF8E7", borderColor: "#E8D9A3" }}>
            <h2 className="font-display text-xl mb-3 flex items-center gap-2" style={{ color: "#5C4A15" }}>
              <ShieldAlert className="w-5 h-5" strokeWidth={1.6} /> 3. Données de santé — Ce que vous devez savoir
            </h2>
            <p className="text-sm leading-relaxed mb-3" style={{ color: "#5C4A15" }}>
              <b>L'infrastructure d'hébergement utilisée aujourd'hui par Mon Cabinet Bien-être n'est pas certifiée
              Hébergeur de Données de Santé (HDS) en France.</b>
            </p>
            <p className="text-sm leading-relaxed mb-3" style={{ color: "#5C4A15" }}>
              En conséquence, vous ne devez pas utiliser Mon Cabinet Bien-être pour stocker des données
              relevant d'un hébergement HDS, notamment :
            </p>
            <ul className="text-sm leading-relaxed space-y-1 list-disc pl-5 mb-3" style={{ color: "#5C4A15" }}>
              <li>Diagnostic médical</li>
              <li>Pathologies identifiées</li>
              <li>Prescriptions, ordonnances, posologies</li>
              <li>Résultats d'examens médicaux, analyses biologiques</li>
              <li>Antécédents médicaux détaillés</li>
              <li>Toute information caractérisant un état de santé</li>
            </ul>
            <p className="text-sm leading-relaxed" style={{ color: "#5C4A15" }}>
              Les champs à caractère médical (antécédents, allergies, traitements, prescriptions) ont été
              <b> désactivés de l'interface</b>. Utilisez uniquement les champs libres pour du suivi
              d'accompagnement bien-être général.
            </p>
          </section>

          {/* 4. Hébergement et transferts */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3 flex items-center gap-2">
              <Server className="w-5 h-5" strokeWidth={1.6} /> 4. Hébergement et localisation des données
            </h2>
            <div className="overflow-x-auto">
              <table className="text-sm w-full border-collapse">
                <tbody>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Hébergeur technique</td>
                    <td className="py-2">Emergent (plateforme SaaS, Kubernetes managé)</td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Localisation des serveurs</td>
                    <td className="py-2">États-Unis et Inde — <i>information officielle Emergent</i></td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Base de données</td>
                    <td className="py-2">MongoDB (managed) — chiffrement au repos AES-256 côté infra + chiffrement applicatif Fernet AES-128-CBC sur les champs sensibles</td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Communications</td>
                    <td className="py-2">Chiffrées en transit (TLS 1.2+ / HTTPS)</td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Certification HDS</td>
                    <td className="py-2" style={{ color: "#B45309" }}><b>Non revendiquée</b> par l'hébergeur pour l'environnement utilisé</td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Certifications de l'hébergeur</td>
                    <td className="py-2">ISO · SOC 2 Type I (SOC 2 Type II en cours) — <i>information officielle Emergent</i></td>
                  </tr>
                  <tr className="border-b" style={{ borderColor: "var(--border)" }}>
                    <td className="py-2 pr-4 font-medium">Région MongoDB exacte</td>
                    <td className="py-2" style={{ color: "#B45309" }}><b>À VÉRIFIER</b> auprès d'Emergent Support</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-medium">DPA (accord de traitement)</td>
                    <td className="py-2" style={{ color: "#B45309" }}><b>À RÉGULARISER</b> — DPA standard non disponible hors offre Enterprise Emergent</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 5. Transferts hors UE */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3 flex items-center gap-2">
              <Globe className="w-5 h-5" strokeWidth={1.6} /> 5. Transferts hors Union européenne
            </h2>
            <p className="text-sm leading-relaxed mb-3">
              L'infrastructure actuelle traite des données hors Union européenne / EEE, notamment
              <b> aux États-Unis et en Inde</b>.
            </p>
            <p className="text-sm leading-relaxed mb-3">
              Inventaire des flux (à confirmer / compléter avec Emergent) :
            </p>
            <ul className="text-sm leading-relaxed space-y-1.5 list-disc pl-5">
              <li>Base MongoDB (fiches clients, agenda, factures, notes) : <b>US / Inde</b></li>
              <li>Sauvegardes automatiques (7 jours point-in-time) : <b>À CONFIRMER par Emergent Support</b> (probablement même zone)</li>
              <li>Logs applicatifs : <b>À CONFIRMER par Emergent Support</b></li>
              <li>Fichiers uploadés (logos factures) : <b>À CONFIRMER par Emergent Support</b></li>
              <li>Paiement Stripe : Stripe Ireland Limited — infrastructure UE + US selon flux</li>
              <li>SMS Twilio (rappels séances) : nom, prénom, heure du RDV, téléphone — Twilio Inc. (US, IE)</li>
              <li>Emails Resend (notifications, drip marketing) : email + contenu — Resend Inc. (US, EU)</li>
              <li>Google Calendar (si activé côté utilisateur) : titre + horaire des séances — Google LLC (US)</li>
            </ul>
            <p className="text-sm leading-relaxed mt-3" style={{ color: "#B45309" }}>
              <b>À RÉGULARISER</b> : mécanisme de transfert international documenté (Clauses Contractuelles
              Types, TIA, DPA signé avec Emergent). En attente de fourniture par l'hébergeur.
            </p>
          </section>

          {/* 6. Droits RGPD */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3">6. Vos droits RGPD</h2>
            <p className="text-sm leading-relaxed mb-3">
              Vous disposez à tout moment des droits d'accès, de rectification, d'effacement, de portabilité,
              d'opposition et de limitation sur vos données personnelles.
            </p>
            <ul className="text-sm leading-relaxed space-y-1.5 list-disc pl-5">
              <li><b>Export</b> : depuis votre espace, chaque fiche client et dossier de séances peut être téléchargée au format PDF.</li>
              <li><b>Suppression du compte</b> : sur demande à <a href="mailto:contact@capsulepatrimoine.com" className="text-sage underline">contact@capsulepatrimoine.com</a>. Suppression effective sous 30 jours.</li>
              <li><b>Réclamation CNIL</b> : vous pouvez adresser une réclamation à la CNIL — <a href="https://www.cnil.fr" target="_blank" rel="noreferrer" className="text-sage underline">www.cnil.fr</a>.</li>
            </ul>
          </section>

          {/* 7. Sécurité */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3">7. Sécurité technique en place</h2>
            <ul className="text-sm leading-relaxed space-y-1.5 list-disc pl-5">
              <li>Chiffrement en transit : TLS 1.2+ / HTTPS sur toutes les communications</li>
              <li>Chiffrement au repos : AES-256 côté infrastructure (MongoDB managed) + Fernet AES-128-CBC + HMAC-SHA256 côté application sur les champs sensibles (notes, motif, objectifs)</li>
              <li>Authentification : mots de passe hashés bcrypt, tokens JWT à expiration</li>
              <li>Isolation multi-utilisateurs : chaque thérapeute n'accède qu'à ses propres données</li>
              <li>Sauvegardes : point-in-time recovery 7 jours (via MongoDB managed)</li>
            </ul>
          </section>

          {/* 8. Points à régulariser */}
          <section className="rounded-xl border p-5" style={{ background: "#FEF2F2", borderColor: "#FCA5A5" }}>
            <h2 className="font-display text-xl mb-3 flex items-center gap-2" style={{ color: "#7F1D1D" }}>
              <FileWarning className="w-5 h-5" strokeWidth={1.6} /> 8. Points à régulariser (transparence)
            </h2>
            <ul className="text-sm leading-relaxed space-y-1.5 list-disc pl-5" style={{ color: "#7F1D1D" }}>
              <li>Signature d'un DPA (accord de traitement RGPD) avec Emergent, définissant contractuellement son rôle au regard du RGPD</li>
              <li>Fourniture des garanties de transferts hors UE (Clauses Contractuelles Types ou mécanisme équivalent) pour les flux vers les États-Unis / Inde</li>
              <li>Confirmation officielle de la localisation exacte des données (région précise des serveurs, sauvegardes, logs)</li>
              <li>Publication de la liste officielle des sous-traitants Emergent</li>
              <li>Documentation formelle des procédures d'incident, de notification de violation de données, de suppression définitive et de fin de contrat</li>
            </ul>
            <p className="text-xs leading-relaxed mt-3" style={{ color: "#7F1D1D" }}>
              Ces points sont en cours de clarification avec l'hébergeur. Cette page sera mise à jour dès
              réception des documents officiels.
            </p>
          </section>

          {/* 9. Contact */}
          <section>
            <h2 className="font-display text-xl text-sage mb-3">9. Contact</h2>
            <p className="text-sm leading-relaxed">
              Toute demande relative à cette politique ou à vos droits RGPD :{" "}
              <a href="mailto:contact@capsulepatrimoine.com" className="text-sage underline">contact@capsulepatrimoine.com</a>
            </p>
          </section>

          <footer className="pt-6 border-t text-xs text-muted-soft" style={{ borderColor: "var(--border)" }}>
            Mon Cabinet Bien-être — édité par Capsule Patrimoine · France
          </footer>
        </div>
      </div>
    </div>
  );
}
